/**
 * Main App Component
 * Prozpector CRM
 */

import { Suspense, lazy } from 'react';
import { Switch, Route } from 'wouter';
import { queryClient } from './lib/queryClient';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import ErrorBoundary from '@/components/ErrorBoundary';
import { AuthProvider } from '@/lib/auth-context';
import { OrganizationProvider } from '@/lib/organization-context';
import { ClientProvider } from '@/lib/client-context';
import { CampaignProvider } from '@/lib/campaign-context';
import { AnalyticsProvider } from '@/components/AnalyticsProvider';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { PortalProvider } from '@/lib/portal-context';
import { PortalProtectedRoute } from '@/components/portal/PortalProtectedRoute';
import { Loader2 } from 'lucide-react';

// Public pages (not code-split)
import LoginPage from '@/pages/login';
import SignupPage from '@/pages/signup';
import ForgotPasswordPage from '@/pages/forgot-password';
import ResetPasswordPage from '@/pages/reset-password';
import SetupMFAPage from '@/pages/setup-mfa';
import LandingPage from '@/pages/marketing/LandingPage';
import MarketingPricingPage from '@/pages/marketing/PricingPage';
import FeaturesPage from '@/pages/marketing/FeaturesPage';
import IntegrationsPage from '@/pages/marketing/IntegrationsPage';
import AboutPage from '@/pages/marketing/AboutPage';
import HelpPage from '@/pages/marketing/HelpPage';
import ContactPage from '@/pages/marketing/ContactPage';
import BlogPage from '@/pages/marketing/BlogPage';
import BlogPostPage from '@/pages/marketing/BlogPostPage';
import PrivacyPage from '@/pages/marketing/PrivacyPage';
import TermsPage from '@/pages/marketing/TermsPage';
import SecurityPage from '@/pages/marketing/SecurityPage';
import DebugProspects from '@/pages/debug-prospects';

// Protected pages (code-split for better performance)
const Dashboard = lazy(() => import('@/pages/dashboard'));
const ProspectsPage = lazy(() => import('@/pages/prospects'));
const PipelinePage = lazy(() => import('@/pages/pipeline'));
const TemplatesPage = lazy(() => import('@/pages/templates'));
const TasksPage = lazy(() => import('@/pages/tasks'));
const SettingsPage = lazy(() => import('@/pages/settings'));
// Phase 2: Automation pages (Lead Scraping moved to Campaign Wizard)
const SequencesPage = lazy(() => import('@/pages/sequences'));
const SequenceBuilderPage = lazy(() => import('@/pages/sequence-builder'));
const TemplateEditorPage = lazy(() => import('@/pages/template-editor'));
// Phase 3: Multi-tenancy pages
const OnboardingPage = lazy(() => import('@/pages/onboarding'));
const PricingPage = lazy(() => import('@/pages/pricing'));
const LimitsPage = lazy(() => import('@/pages/limits'));
// Security pages
const SecuritySettingsPage = lazy(() => import('@/pages/security-settings'));
const AccountSettingsPage = lazy(() => import('@/pages/account-settings'));
// Support and Subscription pages
const SupportPage = lazy(() => import('@/pages/support'));
const AdminSupportPage = lazy(() => import('@/pages/admin-support'));
// const AffiliatesPage = lazy(() => import('@/pages/affiliates')); // Disabled until 10+ customers
const SubscriptionPage = lazy(() => import('@/pages/subscription'));
const SubscriptionSuccessPage = lazy(() => import('@/pages/subscription-success'));
const AppIntegrationsPage = lazy(() => import('@/pages/integrations'));
// Analytics Dashboard (Owner-only)
const AnalyticsDashboard = lazy(() => import('@/components/analytics/OwnerAnalyticsDashboard'));
// Email Analytics (All users)
const EmailAnalyticsPage = lazy(() => import('@/pages/email-analytics'));
// Getting Started Guide
const GettingStartedPage = lazy(() => import('@/pages/getting-started'));
// v2 AI Engine Pages
const ChallengeMapsPage = lazy(() => import('@/pages/challenge-maps'));
const GroundRulesPage = lazy(() => import('@/pages/ground-rules'));
const InboxPage = lazy(() => import('@/pages/inbox'));
const ReviewQueuePage = lazy(() => import('@/pages/review-queue'));
const FlywheelPage = lazy(() => import('@/pages/flywheel'));
const TemplateComponentsPage = lazy(() => import('@/pages/template-components'));
// Client Management
const ClientsPage = lazy(() => import('@/pages/clients'));
const CampaignsPage = lazy(() => import('@/pages/campaigns'));
const CampaignTemplatesWizard = lazy(() => import('@/pages/CampaignTemplatesWizard'));
const CampaignAutomationPage = lazy(() => import('@/pages/campaign-automation'));
const SystemStatusPage = lazy(() => import('@/pages/system-status'));
// Public Invite Pages
const InvitePage = lazy(() => import('@/pages/invite'));
const InviteSuccessPage = lazy(() => import('@/pages/invite-success'));
const InviteErrorPage = lazy(() => import('@/pages/invite-error'));
// Blog Manager (Disabled - not needed for customers)
// const BlogManagerPage = lazy(() => import('@/pages/blog-manager'));

// Client Portal Pages - HIDDEN FOR NOW (keeping code for later)
// const PortalLogin = lazy(() => import('@/pages/portal/login'));
// const PortalForgotPassword = lazy(() => import('@/pages/portal/forgot-password'));
// const PortalResetPassword = lazy(() => import('@/pages/portal/reset-password'));
// const PortalInvite = lazy(() => import('@/pages/portal/invite'));
// const PortalDashboard = lazy(() => import('@/pages/portal/dashboard'));
// const PortalInbox = lazy(() => import('@/pages/portal/inbox'));
// const PortalBookings = lazy(() => import('@/pages/portal/bookings'));
// const PortalBilling = lazy(() => import('@/pages/portal/billing'));
// const PortalOnboarding = lazy(() => import('@/pages/portal/onboarding'));
// const PortalSettings = lazy(() => import('@/pages/portal/settings'));

// Loading fallback component
const LoadingFallback = () => (
  <div className="min-h-screen w-full flex items-center justify-center bg-white">
    <div className="text-center">
      <Loader2 className="h-12 w-12 animate-spin text-primary-600 mx-auto mb-4" />
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AnalyticsProvider>
          <OrganizationProvider>
            <ClientProvider>
              <CampaignProvider>
                <QueryClientProvider client={queryClient}>
                  <TooltipProvider>
                <Toaster />
              <Suspense fallback={<LoadingFallback />}>
              <Switch>
                {/* Public Marketing Routes */}
                <Route path="/" component={LandingPage} />
                <Route path="/features" component={FeaturesPage} />
                <Route path="/pricing" component={MarketingPricingPage} />
                <Route path="/integrations" component={IntegrationsPage} />
                <Route path="/about" component={AboutPage} />
                <Route path="/help" component={HelpPage} />
                <Route path="/contact" component={ContactPage} />
                <Route path="/blog" component={BlogPage} />
                <Route path="/blog/:slug" component={BlogPostPage} />
                <Route path="/privacy" component={PrivacyPage} />
                <Route path="/terms" component={TermsPage} />
                <Route path="/security" component={SecurityPage} />
                <Route path="/debug-prospects" component={DebugProspects} />
                <Route path="/login" component={LoginPage} />
                <Route path="/signup" component={SignupPage} />
                <Route path="/forgot-password" component={ForgotPasswordPage} />
                <Route path="/reset-password" component={ResetPasswordPage} />
                <Route path="/setup-mfa" component={SetupMFAPage} />

                {/* Public Invite Routes (for clients to connect their inbox) */}
                <Route path="/invite/success">
                  <InviteSuccessPage />
                </Route>
                <Route path="/invite/error">
                  <InviteErrorPage />
                </Route>
                <Route path="/invite/:token">
                  <InvitePage />
                </Route>

                {/* Protected App Routes */}
                <Route path="/app/dashboard">
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                </Route>
                <Route path="/dashboard">
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                </Route>
                <Route path="/app">
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                </Route>
                <Route path="/getting-started">
                  <ProtectedRoute>
                    <GettingStartedPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/app/getting-started">
                  <ProtectedRoute>
                    <GettingStartedPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/app/prospects">
                  <ProtectedRoute>
                    <ProspectsPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/prospects">
                  <ProtectedRoute>
                    <ProspectsPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/app/pipeline">
                  <ProtectedRoute>
                    <PipelinePage />
                  </ProtectedRoute>
                </Route>
                <Route path="/pipeline">
                  <ProtectedRoute>
                    <PipelinePage />
                  </ProtectedRoute>
                </Route>
                <Route path="/app/templates">
                  <ProtectedRoute>
                    <TemplatesPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/templates">
                  <ProtectedRoute>
                    <TemplatesPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/app/templates/:id">
                  <ProtectedRoute>
                    <TemplateEditorPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/templates/:id">
                  <ProtectedRoute>
                    <TemplateEditorPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/app/tasks">
                  <ProtectedRoute>
                    <TasksPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/tasks">
                  <ProtectedRoute>
                    <TasksPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/app/settings">
                  <ProtectedRoute>
                    <SettingsPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/settings">
                  <ProtectedRoute>
                    <SettingsPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/app/settings/limits">
                  <ProtectedRoute>
                    <LimitsPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/settings/limits">
                  <ProtectedRoute>
                    <LimitsPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/app/settings/security">
                  <ProtectedRoute>
                    <SecuritySettingsPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/settings/security">
                  <ProtectedRoute>
                    <SecuritySettingsPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/app/settings/account">
                  <ProtectedRoute>
                    <AccountSettingsPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/settings/account">
                  <ProtectedRoute>
                    <AccountSettingsPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/settings/integrations">
                  <ProtectedRoute>
                    <AppIntegrationsPage />
                  </ProtectedRoute>
                </Route>
                {/* Lead Scraping removed - functionality moved to Campaign Wizard Step 4 */}
                <Route path="/app/sequences">
                  <ProtectedRoute>
                    <SequencesPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/sequences">
                  <ProtectedRoute>
                    <SequencesPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/app/sequences/:id">
                  <ProtectedRoute>
                    <SequenceBuilderPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/sequences/:id">
                  <ProtectedRoute>
                    <SequenceBuilderPage />
                  </ProtectedRoute>
                </Route>
                {/* System Status */}
                <Route path="/system-status">
                  <ProtectedRoute>
                    <SystemStatusPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/app/system-status">
                  <ProtectedRoute>
                    <SystemStatusPage />
                  </ProtectedRoute>
                </Route>
                {/* v2 AI Engine Routes */}
                <Route path="/challenge-maps">
                  <ProtectedRoute>
                    <ChallengeMapsPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/app/challenge-maps">
                  <ProtectedRoute>
                    <ChallengeMapsPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/ground-rules">
                  <ProtectedRoute>
                    <GroundRulesPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/app/ground-rules">
                  <ProtectedRoute>
                    <GroundRulesPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/inbox">
                  <ProtectedRoute>
                    <InboxPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/app/inbox">
                  <ProtectedRoute>
                    <InboxPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/review-queue">
                  <ProtectedRoute>
                    <ReviewQueuePage />
                  </ProtectedRoute>
                </Route>
                <Route path="/flywheel">
                  <ProtectedRoute>
                    <FlywheelPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/app/flywheel">
                  <ProtectedRoute>
                    <FlywheelPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/template-components">
                  <ProtectedRoute>
                    <TemplateComponentsPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/app/template-components">
                  <ProtectedRoute>
                    <TemplateComponentsPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/clients">
                  <ProtectedRoute>
                    <ClientsPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/app/clients">
                  <ProtectedRoute>
                    <ClientsPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/campaigns">
                  <ProtectedRoute>
                    <CampaignsPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/campaigns/:id/templates">
                  <ProtectedRoute>
                    <CampaignTemplatesWizard />
                  </ProtectedRoute>
                </Route>
                <Route path="/campaigns/:id/automation">
                  <ProtectedRoute>
                    <CampaignAutomationPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/onboarding">
                  <ProtectedRoute>
                    <OnboardingPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/support">
                  <ProtectedRoute>
                    <SupportPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/admin-support">
                  <ProtectedRoute>
                    <AdminSupportPage />
                  </ProtectedRoute>
                </Route>
                {/* Affiliates disabled until 10+ customers */}
                {/* <Route path="/affiliates">
                  <ProtectedRoute>
                    <AffiliatesPage />
                  </ProtectedRoute>
                </Route> */}
                <Route path="/subscription/success">
                  <SubscriptionSuccessPage />
                </Route>
                <Route path="/subscription">
                  <ProtectedRoute>
                    <SubscriptionPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/analytics">
                  <ProtectedRoute>
                    <AnalyticsDashboard />
                  </ProtectedRoute>
                </Route>
                <Route path="/email-analytics">
                  <ProtectedRoute>
                    <EmailAnalyticsPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/app/analytics">
                  <ProtectedRoute>
                    <AnalyticsDashboard />
                  </ProtectedRoute>
                </Route>
                {/* Blog Manager disabled - not needed for customers */}
                {/* <Route path="/blog-manager">
                  <ProtectedRoute>
                    <BlogManagerPage />
                  </ProtectedRoute>
                </Route>
                <Route path="/app/blog-manager">
                  <ProtectedRoute>
                    <BlogManagerPage />
                  </ProtectedRoute>
                </Route> */}

                {/* Client Portal Routes - HIDDEN FOR NOW (keeping code for later)
                <Route path="/portal/login">
                  <PortalProvider>
                    <PortalLogin />
                  </PortalProvider>
                </Route>
                <Route path="/portal/forgot-password">
                  <PortalProvider>
                    <PortalForgotPassword />
                  </PortalProvider>
                </Route>
                <Route path="/portal/reset-password">
                  <PortalProvider>
                    <PortalResetPassword />
                  </PortalProvider>
                </Route>
                <Route path="/portal/invite/:token">
                  <PortalProvider>
                    <PortalInvite />
                  </PortalProvider>
                </Route>
                <Route path="/portal/dashboard">
                  <PortalProvider>
                    <PortalProtectedRoute>
                      <PortalDashboard />
                    </PortalProtectedRoute>
                  </PortalProvider>
                </Route>
                <Route path="/portal/inbox">
                  <PortalProvider>
                    <PortalProtectedRoute>
                      <PortalInbox />
                    </PortalProtectedRoute>
                  </PortalProvider>
                </Route>
                <Route path="/portal/bookings">
                  <PortalProvider>
                    <PortalProtectedRoute>
                      <PortalBookings />
                    </PortalProtectedRoute>
                  </PortalProvider>
                </Route>
                <Route path="/portal/billing">
                  <PortalProvider>
                    <PortalProtectedRoute>
                      <PortalBilling />
                    </PortalProtectedRoute>
                  </PortalProvider>
                </Route>
                <Route path="/portal/settings">
                  <PortalProvider>
                    <PortalProtectedRoute>
                      <PortalSettings />
                    </PortalProtectedRoute>
                  </PortalProvider>
                </Route>
                <Route path="/portal/onboarding">
                  <PortalProvider>
                    <PortalProtectedRoute skipOnboardingCheck>
                      <PortalOnboarding />
                    </PortalProtectedRoute>
                  </PortalProvider>
                </Route>
                <Route path="/portal/no-access">
                  <PortalProvider>
                    <div className="min-h-screen flex items-center justify-center bg-gray-50">
                      <div className="text-center max-w-md p-6">
                        <h1 className="text-2xl font-bold text-gray-900 mb-4">No Portal Access</h1>
                        <p className="text-gray-600 mb-4">
                          Your account doesn't have access to the client portal.
                          Please contact your account manager.
                        </p>
                        <a href="/portal/login" className="text-indigo-600 hover:underline">
                          Back to Login
                        </a>
                      </div>
                    </div>
                  </PortalProvider>
                </Route>
                */}

                {/* 404 Not Found */}
                <Route>
                  <div className="min-h-screen w-full flex items-center justify-center bg-white">
                    <div className="text-center">
                      <h1 className="text-2xl font-bold text-gray-900 mb-4">Page Not Found</h1>
                      <p className="text-gray-600">Return to the dashboard</p>
                    </div>
                  </div>
                </Route>
              </Switch>
            </Suspense>
                </TooltipProvider>
              </QueryClientProvider>
            </CampaignProvider>
          </ClientProvider>
        </OrganizationProvider>
        </AnalyticsProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
